/**
 * CompOffLedger — R153 · Feb 2026
 *
 * Chronological earned/consumed/expiring view of an employee's
 * comp-off balance. Reads /workspaces/{ws}/comp-off-ledger and shows:
 *   • Summary strip: current balance · earned · consumed · expiring 30d
 *   • Ledger table: date · kind · days · running balance · expires · note
 * Embedded in EmployeeDetail's Leave & Attendance tab.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Wallet, TrendingUp, TrendingDown, Clock, AlertTriangle, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const KIND_STYLES = {
  earned: { label: "Earned", tone: "text-emerald-700 bg-emerald-50 border-emerald-300", Icon: TrendingUp },
  adjusted: { label: "Adjusted", tone: "text-sky-700 bg-sky-50 border-sky-300", Icon: Wallet },
  consumed: { label: "Consumed", tone: "text-rose-700 bg-rose-50 border-rose-300", Icon: TrendingDown },
  expired: { label: "Expired", tone: "text-muted-foreground bg-muted border-border", Icon: Clock },
};

export default function CompOffLedger({ wsId, userId, canManage = false }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [creditOpen, setCreditOpen] = useState(false);

  const load = async () => {
    if (!wsId || !userId) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/workspaces/${wsId}/comp-off-ledger`, { params: { user_id: userId } });
      setData(data);
    } catch (e) {
      const msg = apiErrorMessage(e, "Failed to load ledger");
      if (e?.response?.status !== 403) toast.error(msg);
      setData({ entries: [], summary: {}, rule: {}, error: msg });
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [wsId, userId]);

  if (busy && !data) return <div className="text-sm text-muted-foreground p-4">Loading comp-off ledger…</div>;
  if (!data) return null;
  if (data.error) return <div className="text-xs text-muted-foreground p-3 border border-dashed border-border">{data.error}</div>;

  const s = data.summary || {};
  const entries = data.entries || [];
  const rule = data.rule || {};
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-3" data-testid="comp-off-ledger">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h4 className="text-sm font-semibold flex items-center gap-1.5"><Wallet className="w-3.5 h-3.5" /> Comp-Off ledger</h4>
        {canManage && (
          <Button size="sm" variant="outline" className="rounded-none h-8" onClick={() => setCreditOpen(true)} data-testid="comp-off-credit-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add entry
          </Button>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="comp-off-summary">
        <SummaryTile label="Current balance" value={s.current_balance ?? 0} accent="primary" />
        <SummaryTile label="Total earned" value={s.total_earned ?? 0} accent="emerald" />
        <SummaryTile label="Total consumed" value={s.total_consumed ?? 0} accent="rose" />
        <SummaryTile label="Expiring in 30d" value={s.expiring_in_30d ?? 0} accent={s.expiring_in_30d > 0 ? "amber" : "slate"} />
      </div>
      {rule.expires_in_days > 0 && (
        <div className="text-[10px] text-muted-foreground">
          Rule: comp-off earned here expires after <b>{rule.expires_in_days} days</b>
          {rule.can_encash ? " · encashable" : ""}
          {rule.carry_forward ? " · carries forward at year-end" : ""}
          {rule.location && rule.location !== "*" ? ` · location ${rule.location}` : ""}
        </div>
      )}
      {entries.length === 0 ? (
        <div className="border border-dashed border-border p-6 text-center text-xs text-muted-foreground" data-testid="comp-off-ledger-empty">
          No comp-off activity yet. Weekend / holiday work over the threshold will show here once HR credits it.
        </div>
      ) : (
        <div className="border border-border overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-muted/40 border-b border-border">
              <tr>
                <th className="text-left px-3 py-2 uppercase tracking-widest text-[10px] font-semibold w-28">Date</th>
                <th className="text-left px-3 py-2 uppercase tracking-widest text-[10px] font-semibold w-28">Kind</th>
                <th className="text-right px-3 py-2 uppercase tracking-widest text-[10px] font-semibold w-20">Days</th>
                <th className="text-right px-3 py-2 uppercase tracking-widest text-[10px] font-semibold w-24">Balance</th>
                <th className="text-left px-3 py-2 uppercase tracking-widest text-[10px] font-semibold w-28">Expires</th>
                <th className="text-left px-3 py-2 uppercase tracking-widest text-[10px] font-semibold">Note</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => {
                const style = KIND_STYLES[e.kind] || KIND_STYLES.earned;
                const Icon = style.Icon;
                const nearExpiry = e.expires_on && !e.expired && e.days > 0 && e.expires_on <= addDays(today, 30);
                return (
                  <tr key={e.id} className={`border-t border-border ${e.expired ? "opacity-60" : ""}`} data-testid={`comp-off-row-${e.id}`}>
                    <td className="px-3 py-2 font-mono tabular-nums">{e.date || "—"}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 border text-[10px] font-semibold uppercase tracking-widest ${style.tone}`}>
                        <Icon className="w-2.5 h-2.5" /> {style.label}
                      </span>
                    </td>
                    <td className={`px-3 py-2 text-right font-mono font-semibold ${e.days > 0 ? "text-emerald-700" : e.days < 0 ? "text-rose-700" : ""}`}>
                      {e.days > 0 ? "+" : ""}{e.days}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{e.running_balance}</td>
                    <td className="px-3 py-2 font-mono tabular-nums text-[11px]">
                      {e.expires_on ? (
                        <span className={nearExpiry ? "text-amber-700 font-semibold inline-flex items-center gap-1" : e.expired ? "line-through text-muted-foreground" : "text-muted-foreground"}>
                          {nearExpiry && <AlertTriangle className="w-3 h-3" />}
                          {e.expires_on}
                        </span>
                      ) : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground max-w-[280px]">
                      <div className="truncate" title={e.note}>{e.note || "—"}</div>
                      {e.actor_name && <div className="text-[9px]">by {e.actor_name}</div>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {creditOpen && (
        <CreditDialog
          wsId={wsId}
          userId={userId}
          onClose={() => setCreditOpen(false)}
          onSaved={() => { setCreditOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function SummaryTile({ label, value, accent }) {
  const bg = {
    primary: "bg-primary/10 border-primary/30 text-primary",
    emerald: "bg-emerald-50 border-emerald-300 text-emerald-800",
    rose: "bg-rose-50 border-rose-300 text-rose-800",
    amber: "bg-amber-50 border-amber-300 text-amber-800",
    slate: "bg-muted border-border text-foreground",
  }[accent] || "bg-muted border-border text-foreground";
  return (
    <div className={`border p-2 ${bg}`}>
      <div className="text-[10px] uppercase tracking-widest opacity-80">{label}</div>
      <div className="text-lg font-bold mt-0.5 font-mono">{Number(value).toFixed(2)}</div>
    </div>
  );
}

function CreditDialog({ wsId, userId, onClose, onSaved }) {
  const [days, setDays] = useState("1");
  const [kind, setKind] = useState("earned");
  const [earnDate, setEarnDate] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!days || !note.trim()) { toast.error("Days + note required"); return; }
    setBusy(true);
    try {
      await api.post(`/workspaces/${wsId}/comp-off-ledger/entry`, {
        user_id: userId, days: Number(days), kind, earn_date: earnDate, note: note.trim(),
      });
      toast.success("Entry added");
      onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to add")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="comp-off-credit-dialog">
        <DialogHeader><DialogTitle>Add comp-off entry</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Days (+ credit / − debit)</Label>
              <Input type="number" step="0.25" value={days} onChange={(e) => setDays(e.target.value)} className="h-9 text-sm mt-1" data-testid="comp-off-credit-days" />
            </div>
            <div>
              <Label className="text-xs">Kind</Label>
              <select value={kind} onChange={(e) => setKind(e.target.value)} className="w-full h-9 text-sm mt-1 bg-background border border-border px-2" data-testid="comp-off-credit-kind">
                <option value="earned">Earned</option>
                <option value="adjusted">Adjusted</option>
                <option value="expired">Expired</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Earn date</Label>
              <Input type="date" value={earnDate} onChange={(e) => setEarnDate(e.target.value)} className="h-9 text-sm mt-1" data-testid="comp-off-credit-date" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Note *</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Compensating Sunday shift on 2026-02-10" className="h-9 text-sm mt-1" data-testid="comp-off-credit-note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={busy} data-testid="comp-off-credit-save">
            {busy ? "Saving…" : "Add entry"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function addDays(iso, days) {
  try {
    const d = new Date(iso);
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
  } catch { return iso; }
}
